import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useSnackbar,
} from '@chronosphereio/chrono-ui';
import { ClipboardOutline as Clipboard, Refresh } from 'mdi-material-ui';
import { useState } from 'react';
import { ListVariableDefinition } from '../../../../core';
import { TOOLTIP_TEXT } from '../../../constants';
import { useListVariablePluginValues } from '../variable-model';

const DEFAULT_MAX_PREVIEW_VALUES = 50;

interface VariablePreviewProps {
  values?: string[];
  onRefresh?: () => void;
  isLoading?: boolean;
  error?: string;
}

export function VariablePreview(props: VariablePreviewProps) {
  const { values: rawValues, onRefresh, isLoading, error } = props;
  const [maxValues, setMaxValues] = useState<number | undefined>(DEFAULT_MAX_PREVIEW_VALUES);
  const { infoSnackbar } = useSnackbar();
  const showAll = () => {
    setMaxValues(undefined);
  };
  let notShown = 0;

  const values = rawValues?.filter((val) => val !== '');

  if (values && values?.length > 0 && maxValues) {
    notShown = values.length - maxValues;
  }

  return (
    <div>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography variant="h4">Preview values</Typography>
        {onRefresh && (
          <Tooltip Content={TOOLTIP_TEXT.refreshVariableValues}>
            <IconButton onClick={onRefresh}>
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip Content={TOOLTIP_TEXT.copyVariableValues}>
          <IconButton
            onClick={async () => {
              if (values?.length) {
                await navigator.clipboard.writeText(values.map((value) => value).join(', '));
                infoSnackbar('Preview values copied to clipboard!');
              }
            }}
          >
            <Clipboard />
          </IconButton>
        </Tooltip>
      </Stack>
      <Card variant="outlined">
        <Stack direction="row" flexWrap="wrap" gap={1} m={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {!isLoading && values?.length === 0 && <Alert severity="info">No results</Alert>}
          {isLoading && (
            <Stack width="100%" sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Stack>
          )}
          {values?.slice(0, maxValues).map((val) => (
            <Chip key={val} label={val} />
          ))}
          {notShown > 0 && <Chip onClick={showAll} label={`+${notShown} more`} />}
        </Stack>
      </Card>
    </div>
  );
}

interface VariableListPreviewProps {
  definition: ListVariableDefinition;
  onRefresh: () => void;
}

export function VariableListPreview(props: VariableListPreviewProps) {
  const { definition, onRefresh } = props;
  const { data, isFetching, error } = useListVariablePluginValues(definition);
  const errorMessage = (error as Error)?.message;

  return (
    <VariablePreview
      values={data?.map((val) => val.value) || []}
      onRefresh={onRefresh}
      isLoading={isFetching}
      error={errorMessage}
    />
  );
}
